import dayjs, { storeTimeZone } from "./dayjs";

export const convertToUtc = (val) => {
  return val ? new Date(val).toISOString() : "";
};

// ── Date-range boundaries ────────────────────────────────────────────────────
// Use these for EVERY from/to filter instead of convertToUtc.
//
// The pickers hand back a date with no meaningful time (often literally
// "MM/DD/YYYY"), so `convertToUtc(toDate)` produced MIDNIGHT AT THE START of the
// end day. Backends filter `date <= toDate`, so the end day was excluded
// entirely — picking 10-Jul → 19-Jul returned records only up to the 18th.
// Worse, on a UTC+n browser that midnight converts to the previous day in UTC,
// so the last n hours of the 18th vanished too.
//
// toRangeEndUtc snaps to 23:59:59.999 of the selected day in the STORE'S
// timezone, then converts to UTC for the API. toRangeStartUtc does the same at
// 00:00:00.000.
//
// ── Why the store zone and not the device's ──
// These used to snap with a bare `dayjs(val)`, i.e. the DEVICE zone, and that was
// right while dates were also DISPLAYED in the device zone. The 2026-08-10
// timezone fix pointed getTimeZone() at storeTimeZone(), so every date now renders
// in the shop's zone — and these were left behind. The result was two clocks in one
// request: a range cut on the viewer's midnight, rows labelled with the shop's.
// Invisible while every till's Windows clock matches the shop; wrong for an owner
// reviewing from another country, and wrong for any browser on UTC.
//
// A shop's "10 August" is a fact about the shop, not about who is looking at it.
//
// ── Idempotency, which is load-bearing ──
// Export.jsx re-applies these over a param DynamicTable has ALREADY converted, so
// snapping twice must give the same answer. That means the two input shapes have
// to be told apart:
//
//   a zone-less calendar day  ("08/10/2026", a Date at local midnight, a dayjs)
//       → the day the user pointed at; read its Y-M-D as written.
//   a full UTC instant        ("2026-08-09T21:00:00.000Z")
//       → already one of our own boundaries; read its Y-M-D IN THE STORE ZONE.
//
// Reading an instant in the device zone instead is what would break it: on a UTC
// browser, Kuwait's "10 Aug 00:00" is the instant 09 Aug 21:00, whose device-zone
// calendar day is the 9th — so a second pass would silently shift the export back
// a whole day.
const hasZoneSuffix = (val) =>
  typeof val === "string" && /(?:Z|[+-]\d{2}:?\d{2})$/.test(val.trim());

// Which CALENDAR DAY does this value denote? The two input shapes described
// above, resolved in one place: a zone-less value is read as written, a full UTC
// instant is read in the store zone. Returns "YYYY-MM-DD", or "" for junk.
//
// Exported because a range boundary is not the only thing built from it —
// utils/reportFilterLink puts the same day in a drill-through URL. Two rules for
// "the day the user pointed at" would eventually disagree by one day, which is
// exactly the class of bug this file exists to prevent.
export const toCalendarDay = (val) => {
  if (!val || !dayjs(val).isValid()) return "";

  try {
    return hasZoneSuffix(val)
      ? dayjs.utc(val).tz(storeTimeZone()).format("YYYY-MM-DD")
      : dayjs(val).format("YYYY-MM-DD");
  } catch {
    return dayjs(val).format("YYYY-MM-DD");
  }
};

const snapDay = (val, edge) => {
  if (!val || !dayjs(val).isValid()) return "";

  const zone = storeTimeZone();

  try {
    return dayjs.tz(toCalendarDay(val), zone)[edge]("day").toISOString();
  } catch {
    // Unknown IANA id (or the tz plugin unavailable) — fall back to the old
    // device-zone behaviour rather than returning nothing.
    return dayjs(val)[edge]("day").toISOString();
  }
};

export const toRangeStartUtc = (val) => snapDay(val, "startOf");

export const toRangeEndUtc = (val) => snapDay(val, "endOf");

// ── The company's "today", and a stored date as the company's calendar day ───────
// A business document's date is a day in the SHOP's calendar. Forms used to default it
// to the browser's day (`new Date()`, `dayjs()`), so an owner in Dhaka at 01:20 on 5 Oct
// created a Berlin voucher dated 5 Oct while the shop's calendar still said 4 Oct — and the
// list, the GL and the PDF then showed the 4th. Every document form defaults through these.

// Now, on the shop's clock (device clock if the configured zone is unusable).
export const storeNow = () => {
  try {
    return dayjs().tz(storeTimeZone());
  } catch {
    return dayjs();
  }
};

// Today in the shop's calendar, "YYYY-MM-DD".
export const storeTodayYmd = () => storeNow().format("YYYY-MM-DD");

// Days from the shop's today to a "YYYY-MM-DD…" calendar date; negative = passed, null
// when there is no date. Calendar arithmetic only — the time part is ignored, so a quote
// valid until the 27th is still valid all of the 27th wherever the browser sits.
export const daysUntil = (value) => {
  if (!value) return null;
  const day = (s) => {
    const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((day(value) - day(storeTodayYmd())) / 86400000);
};

// The shop's midnight today as a UTC instant — the value a document-date field stores
// (the same shape toRangeStartUtc gives a picked day).
export const storeDayStartUtc = () => toRangeStartUtc(storeTodayYmd());

// The shop's calendar day of a stored document date, as a plain dayjs at local midnight,
// for handing to InputDate (which shows whatever day it is given, in the device's zone).
//
//   - a value the picker just produced (dayjs / Date): the day the user pointed at;
//   - a bare "YYYY-MM-DD": that day;
//   - anything else is an instant from the server. The API returns those WITHOUT a "Z"
//     (see utils/dayjs.js), and every one of them is UTC, so it is read as UTC and turned
//     into the shop's day. Read as written, the shop's midnight east of UTC
//     ("2026-10-04T22:00:00" for Berlin's 5 Oct) showed the day before.
export const toStoreDay = (val) => {
  if (!val) return null;
  if (dayjs.isDayjs(val) || val instanceof Date) {
    const d = dayjs(val);
    return d.isValid() ? d.startOf("day") : null;
  }
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return dayjs(s);
  try {
    const d = dayjs.utc(s);
    return d.isValid() ? dayjs(d.tz(storeTimeZone()).format("YYYY-MM-DD")) : null;
  } catch {
    return dayjs(s);
  }
};

export const combineUtcDateTime = (date, time) => {
  const d = dayjs.utc(date);
  const t = dayjs.utc(time);

  return d.hour(t.hour()).minute(t.minute()).second(t.second()).millisecond(0);
};

// The timezone the SERVER should format dates in — i.e. the business's zone.
//
// Every one of this function's ~14 call sites sends the result to the backend as
// `param.TimeZone`, which MethodService.FormatDate uses to render dates into the
// strings that appear in lists, Excel, PDF and print-outs. It used to return
// `Intl.DateTimeFormat().resolvedOptions().timeZone` — the DEVICE's zone — so a
// till left on the wrong Windows timezone re-dated every server-rendered document,
// while receipts (which render client-side via toStoreTime) used the STORE's zone.
// Same invoice, two clocks, and clients reported it as "the time is one hour out".
//
// Now both paths resolve to the same zone. storeTimeZone() still falls back to the
// device zone when the company has no Time Zone configured, which reproduces the
// old behaviour rather than sending something wrong.
//
// If you genuinely want the viewer's own zone, call dayjs.tz.guess() and say why.
export const getTimeZone = () => storeTimeZone();

export const GetHours = (
  startTime = dayjs().hour(8).minute(0).second(0),
  endTime = dayjs().hour(22).minute(0).second(0),
) => {
  const timeList = [];

  let currentTime = startTime;
  while (currentTime.isSameOrBefore(endTime)) {
    timeList.push(currentTime.format("h:mm A"));
    currentTime = currentTime.add(1, "hour");
  }
  return timeList;
};

export const GetMonths = () => {
  const date = new Date();
  return [
    `01/${date.getFullYear()}`,
    `02/${date.getFullYear()}`,
    `03/${date.getFullYear()}`,
    `04/${date.getFullYear()}`,
    `05/${date.getFullYear()}`,
    `06/${date.getFullYear()}`,
    `07/${date.getFullYear()}`,
    `08/${date.getFullYear()}`,
    `09/${date.getFullYear()}`,
    `10/${date.getFullYear()}`,
    `11/${date.getFullYear()}`,
    `12/${date.getFullYear()}`,
  ];
};

export const getDaysByMonth = (month) => {
  const daysInMonth = dayjs(month, "YYYY/MM").daysInMonth();
  return Array.from({ length: daysInMonth }, (v, k) => k + 1);
};

export const getCurrentWeek = () => {
  const today = dayjs();
  const startOfWeek = today.startOf("week");
  const endOfWeek = today.endOf("week");

  const weekDays = [];
  let currentDay = startOfWeek;

  while (currentDay.isSameOrBefore(endOfWeek, "day")) {
    weekDays.push(currentDay.format("MMM DD"));
    currentDay = currentDay.add(1, "day");
  }

  return weekDays;
};

export const GetDueDate = (date) => {
  return `${dayjs(date, "mm/DD/yyyy").format("DD")}/${dayjs().format(
    "MM/yyyy",
  )}`;
};
